/**
 * @fileoverview Lenis Smooth Scroll Configuration
 * @description Utility for initializing and managing Lenis smooth scroll
 */

import Lenis from '@studio-freight/lenis';

let lenisInstance = null;

/**
 * Initialize Lenis smooth scroll
 * @param {Object} options - Lenis configuration options
 * @returns {Lenis} Lenis instance
 */
export function initSmoothScroll(options = {}) {
  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    // Skip smooth scroll if user prefers reduced motion
    return null;
  }

  if (lenisInstance) {
    return lenisInstance;
  }

  lenisInstance = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    smoothWheel: true,
    wheelMultiplier: 1,
    smoothTouch: false,
    touchMultiplier: 2,
    infinite: false,
    wrapper: window, // Scroll the window, not a specific element
    content: document.documentElement, // Content to scroll
    ...options
  });

  // Intercept wheel events to prevent Lenis from handling scroll on prevented elements
  const wheelHandler = (e) => {
    // Check if the event target or any parent has data-lenis-prevent
    let target = e.target;
    let preventElement = null;
    
    // Walk up the DOM tree to find data-lenis-prevent
    while (target && target !== document.body) {
      if (target.hasAttribute && target.hasAttribute('data-lenis-prevent')) {
        preventElement = target;
        break;
      }
      target = target.parentElement;
    }
    
    if (preventElement) {
      // Find the actual scrollable element
      let scrollableElement = preventElement;
      
      // Check if the prevent element itself is scrollable
      if (preventElement.scrollHeight <= preventElement.clientHeight) {
        // If not scrollable, look for scrollable children with data-lenis-prevent
        const scrollableChildren = Array.from(preventElement.querySelectorAll('[data-lenis-prevent]'));
        for (const child of scrollableChildren) {
          if (child.scrollHeight > child.clientHeight) {
            scrollableElement = child;
            break;
          }
        }
      }
      
      // Check if we found a scrollable element
      const isScrollable = scrollableElement && scrollableElement.scrollHeight > scrollableElement.clientHeight;
      
      if (isScrollable) {
        const isAtTop = scrollableElement.scrollTop <= 0;
        const isAtBottom = scrollableElement.scrollTop + scrollableElement.clientHeight >= scrollableElement.scrollHeight - 1;
        
        // If we can scroll in this direction, prevent Lenis from handling it
        if (!(isAtTop && e.deltaY < 0) && !(isAtBottom && e.deltaY > 0)) {
          // Allow native scrolling - prevent Lenis from intercepting
          e.stopPropagation();
          e.stopImmediatePropagation();
          return;
        }
      }
      
      // Always prevent Lenis when we're inside a data-lenis-prevent element
      // This ensures sidepanes don't trigger window scrolling
      e.stopPropagation();
      e.stopImmediatePropagation();
      return;
    }
  };

  // Add wheel event listener in capture phase to intercept before Lenis
  document.addEventListener('wheel', wheelHandler, { passive: false, capture: true });

  // Store handler for cleanup
  lenisInstance._wheelHandler = wheelHandler;

  // Animation frame loop
  function raf(time) {
    lenisInstance.raf(time);
    requestAnimationFrame(raf);
  }

  requestAnimationFrame(raf);

  return lenisInstance;
}

/**
 * Get current Lenis instance
 * @returns {Lenis|null} Current Lenis instance or null
 */
export function getLenisInstance() {
  return lenisInstance;
}

/**
 * Destroy Lenis instance
 */
export function destroySmoothScroll() {
  if (lenisInstance) {
    // Remove wheel event handler if it was added
    if (lenisInstance._wheelHandler) {
      document.removeEventListener('wheel', lenisInstance._wheelHandler, { capture: true });
    }
    lenisInstance.destroy();
    lenisInstance = null;
  }
}

/**
 * Scroll to target element or position
 * @param {string|number|HTMLElement} target - Target to scroll to
 * @param {Object} options - Scroll options
 */
export function scrollTo(target, options = {}) {
  if (lenisInstance) {
    lenisInstance.scrollTo(target, options);
  } else {
    // Fallback to native scroll
    if (typeof target === 'string') {
      const element = document.querySelector(target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', ...options });
      }
    } else if (typeof target === 'number') {
      window.scrollTo({ top: target, behavior: 'smooth', ...options });
    } else if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: 'smooth', ...options });
    }
  }
}
